import {
  BdsGrid,
  BdsIcon,
  BdsPagination,
  BdsSkeleton,
  BdsTableBody,
  BdsTableHeader,
  BdsTableRow,
  BdsTableTh,
  BdsTypo,
} from 'blip-ds/dist/blip-ds-react';
import React, { useMemo, useCallback } from 'react';
import { PaginationRow, SkeletonTableCell, SortHeaderWrapper, StateContent, Table, TABLE_HEADER_HEIGHT_PX, TABLE_ROW_HEIGHT_PX, TableContainer } from './styles';
import EmptySearchState from './EmptySearchState/EmptySearchState';
import LoadErrorState from './LoadErrorState/LoadErrorState';
import { useCommon } from '../../contexts/CommonContext';
import { TableWithPaginationProps, DEFAULT_STARTED_PAGE } from './types';
import { getLanguageCode } from 'utils/language';

/**
 * A reusable table component with pagination, loading states, and error handling
 *
 * @param props - The component props
 * @returns A table with pagination controls and state management
 */
const TableWithPagination = ({
  options,
  children,
  onPaginationChange,
  onItemsPerPageChange,
  onRefreshData,
}: TableWithPaginationProps): React.ReactElement => {
  const {
    common: { language },
  } = useCommon();

  // Memoize language code calculation
  const languageCode = useMemo(() => {
    return getLanguageCode(language);
  }, [language, getLanguageCode]);

  // Memoize pages calculation
  const totalPages = useMemo(() => {
    if (!options.numberOfItems || !options.itemsPerPage || options.numberOfItems <= 0) {
      return 0;
    }
    return Math.ceil(options.numberOfItems / options.itemsPerPage);
  }, [options.numberOfItems, options.itemsPerPage]);

  // Memoize should show pagination
  const shouldShowPagination = useMemo(() => {
    return Boolean(options.numberOfItems && options.itemsPerPage);
  }, [options.numberOfItems, options.itemsPerPage]);

  // Memoize loading state rows
  const loadingStateRows = useMemo(() => {
    return Array.from({ length: options.itemsPerPage }, (_, rowIndex) => (
      <BdsTableRow key={`loading-row-${rowIndex}`}>
        {options.heading.map((_, columnIndex) => (
          <SkeletonTableCell key={`skeleton-cell-${rowIndex}-${columnIndex}`}>
            <BdsSkeleton height="16px" width="100%" />
          </SkeletonTableCell>
        ))}
      </BdsTableRow>
    ));
  }, [options.itemsPerPage, options.heading]);

  const renderLoadingState = useCallback(() => loadingStateRows, [loadingStateRows]);

  const renderLoadErrorState = useCallback(
    () => (
      <StateContent colSpan={options.heading.length}>
        <LoadErrorState onTryAgain={onRefreshData} />
      </StateContent>
    ),
    [options.heading.length, onRefreshData],
  );

  const renderEmptyState = useCallback(
    () => (
      <StateContent colSpan={options.heading.length}>
        <EmptySearchState subtitle={options.emptyStateSubtitle} />
      </StateContent>
    ),
    [options.heading.length, options.emptyStateSubtitle],
  );

  const renderTableBody = useCallback(() => {
    if (options.isLoading) {
      return renderLoadingState();
    }

    if (children) {
      return children;
    }

    if (options.hasLoadingError) {
      return renderLoadErrorState();
    }

    if (options.hasEmptyState) {
      return renderEmptyState();
    }

    return null;
  }, [
    options.isLoading,
    options.hasLoadingError,
    options.hasEmptyState,
    children,
    renderLoadingState,
    renderLoadErrorState,
    renderEmptyState,
  ]);

  // Memoize table header
  const tableHeader = useMemo(
    () => (
      <BdsTableHeader>
        <BdsTableRow>
          {options.heading.map((item, index) => {
            const columnKey = options.columnKeys?.[index];
            const isSortable = Boolean(columnKey && options.onSortChange);
            const isActiveSort = isSortable && options.sortConfig?.column === columnKey;
            const iconName = isActiveSort && options.sortConfig?.order === 'desc' ? 'arrow-down' : 'arrow-up';

            return (
              <BdsTableTh
                key={`header-${item}-${index}`}
                style={options.columnWidths?.[index] ? { width: options.columnWidths[index] } : undefined}
              >
                {isSortable && columnKey ? (
                  <SortHeaderWrapper onClick={() => options.onSortChange?.(columnKey)}>
                    <BdsTypo variant="fs-14" bold="bold">
                      {item}
                    </BdsTypo>
                    <BdsIcon size="small" name={iconName} className={isActiveSort ? '' : 'sort-arrow-inactive'} />
                  </SortHeaderWrapper>
                ) : (
                  <BdsTypo variant="fs-14" bold="bold">
                    {item}
                  </BdsTypo>
                )}
              </BdsTableTh>
            );
          })}
        </BdsTableRow>
      </BdsTableHeader>
    ),
    [options.heading, options.columnWidths, options.columnKeys, options.sortConfig, options.onSortChange],
  );

  const ROWS_HEIGHT_VALUE = useMemo(() => {
    if (options.isAutoHeight && options.itemsPerPage) {
      if (options.isLoading) {
        return `${options.itemsPerPage * TABLE_ROW_HEIGHT_PX + TABLE_HEADER_HEIGHT_PX}px`;
      }
      const rowCount = React.Children.count(children);
      if (rowCount > 0) {
        return `${rowCount * TABLE_ROW_HEIGHT_PX + TABLE_HEADER_HEIGHT_PX}px`;
      }
    }
    return undefined;
  }, [options.isAutoHeight, options.itemsPerPage, options.isLoading, children]);

  return (
    <BdsGrid data-testid="custom-redirect-table" direction="column">
      <TableContainer shouldHideBorderBottom={!shouldShowPagination} minHeight={ROWS_HEIGHT_VALUE}>
        <Table backgroundColor={options.backgroundColor}>
          {tableHeader}
          <BdsTableBody>{renderTableBody()}</BdsTableBody>
        </Table>
      </TableContainer>
      <PaginationRow backgroundColor={options.backgroundColor}>
        {shouldShowPagination ? (
          <BdsPagination
            number-items={options.numberOfItems ?? 0}
            itemsPage={options.itemsPerPageList}
            started-page={DEFAULT_STARTED_PAGE}
            pageCounter={true}
            onBdsPaginationChange={onPaginationChange}
            onBdsItemsPerPageChange={onItemsPerPageChange}
            pages={totalPages}
            language={languageCode}
          />
        ) : null}
      </PaginationRow>
    </BdsGrid>
  );
};

export default TableWithPagination;
